import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { toast } from 'react-hot-toast';
import { Shield, Search, AlertCircle, Check, X, Info, Lock, Save } from 'lucide-react';
import { USERS_PERMISSIONS } from '../config/accessControl';
import { roleService, userService } from '..';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const PERMISSION_GROUP_LABELS = {
  dashboard: 'Dashboard',
  projects: 'Projects',
  team: 'Team',
  resources: 'Resources',
  training: 'Training',
  reports: 'Reports',
  tasks: 'Tasks',
  admin: 'Admin Functions',
  clients: 'Clients',
  competitions: 'Competitions',
  marketing: 'Marketing',
  pr: 'Public Relations',
  graphics: 'Graphics',
  calendar: 'Calendar',
};

const toTitle = (value) =>
  (value || '')
    .replace(/[_.]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const toRoleKey = (role) => {
  if (!role) {
    return '';
  }

  const defaultRoleMatch = role.description?.match(/default\s+([a-z_]+)\s+role/i);
  if (defaultRoleMatch?.[1]) {
    return defaultRoleMatch[1].toLowerCase();
  }

  return (role.name || '').trim().toLowerCase().replace(/\s+/g, '_');
};

const normalizeRoleForUi = (role, permissionIds = []) => {
  const rolePermissions = role?.permissions || {};
  const normalizedPermissions = {};

  permissionIds.forEach((permissionId) => {
    normalizedPermissions[permissionId] = rolePermissions[permissionId] === true;
  });

  Object.keys(rolePermissions).forEach((permissionId) => {
    if (!(permissionId in normalizedPermissions)) {
      normalizedPermissions[permissionId] = rolePermissions[permissionId] === true;
    }
  });

  return {
    ...role,
    key: toRoleKey(role),
    isSystem: Boolean(role?.is_system ?? role?.isSystem),
    permissions: normalizedPermissions,
  };
};

const buildPermissionGroups = (permissionIds) => {
  const grouped = permissionIds.reduce((acc, permissionId) => {
    const [prefix, suffix] = permissionId.split('.');
    const groupKey = prefix || 'other';
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }

    acc[groupKey].push({
      id: permissionId,
      label: toTitle(suffix || permissionId),
      description: `Allow ${toTitle(suffix || permissionId).toLowerCase()} access in ${PERMISSION_GROUP_LABELS[groupKey] || toTitle(groupKey)}.`,
    });

    return acc;
  }, {});

  return Object.keys(grouped)
    .sort((a, b) => {
      const labelA = PERMISSION_GROUP_LABELS[a] || toTitle(a);
      const labelB = PERMISSION_GROUP_LABELS[b] || toTitle(b);
      return labelA.localeCompare(labelB);
    })
    .map((key) => ({
      page: PERMISSION_GROUP_LABELS[key] || toTitle(key),
      permissions: grouped[key].sort((a, b) => a.id.localeCompare(b.id)),
    }));
};

const PermissionsControl = () => {
  const navigate = useNavigate();
  const { user, rolePermissions } = useApiAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [editedPermissions, setEditedPermissions] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editedUserRoles, setEditedUserRoles] = useState({});
  const [activeTab, setActiveTab] = useState('roles');

  const canManagePermissions =
    user?.role === 'admin' || rolePermissions?.[USERS_PERMISSIONS.MANAGE_PERMISSIONS] === true;

  const permissionsByPage = useMemo(() => buildPermissionGroups(permissionCatalog), [permissionCatalog]);

  const getUsersForRole = useCallback(
    (role) => {
      const roleId = String(role?.id || '').toLowerCase();
      const roleKey = String(role?.key || '').toLowerCase();
      const roleNameKey = String(role?.name || '').trim().toLowerCase().replace(/\s+/g, '_');

      return users.filter((userData) => {
        const userRole = String(userData?.role || '').toLowerCase();
        return userRole === roleKey || userRole === roleId || userRole === roleNameKey;
      });
    },
    [users]
  );

  const loadPermissionCatalog = useCallback(async () => {
    const response = await roleService.getPermissionsCatalog();
    const permissions = Array.isArray(response?.permissions)
      ? response.permissions
      : Array.isArray(response)
      ? response
      : [];

    setPermissionCatalog(permissions);
    return permissions;
  }, []);

  const loadRoles = useCallback(async (permissions = []) => {
    const response = await roleService.getRoles();
    const roleItems = Array.isArray(response) ? response : (response?.roles || []);
    const rolesData = roleItems.map((role) => normalizeRoleForUi(role, permissions));
    setRoles(rolesData);
    return rolesData;
  }, []);

  const loadAllUsers = useCallback(async () => {
    const usersResponse = await userService.getAllUsers();
    const usersData = Array.isArray(usersResponse) ? usersResponse : (usersResponse?.users || []);
    setUsers(usersData);
    return usersData;
  }, []);

  const loadRolesAndUsers = useCallback(async () => {
    setLoading(true);

    const permissions = await loadPermissionCatalog();

    const [rolesResult, usersResult] = await Promise.allSettled([
      loadRoles(permissions),
      loadAllUsers(),
    ]);

    if (rolesResult.status === 'rejected') {
      console.error('Error loading roles:', rolesResult.reason);
      toast.error('Failed to load roles');
    }

    if (usersResult.status === 'rejected') {
      console.error('Error loading users:', usersResult.reason);
      toast.error('Failed to load users');
    }

    setLoading(false);
  }, [loadAllUsers, loadPermissionCatalog, loadRoles]);

  // Only allow admin access to this page
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!canManagePermissions) {
      toast.error('You do not have permission to access this page');
      navigate('/');
      return;
    }

    loadRolesAndUsers();
  }, [user, canManagePermissions, navigate, loadRolesAndUsers]);

  const handleEditRolePermissions = (role) => {
    setSelectedRole(role);
    setEditedPermissions(role.permissions || {});
    setIsModalOpen(true);
  };

  const handlePermissionChange = (permissionId, value) => {
    setEditedPermissions(prev => ({
      ...prev,
      [permissionId]: value
    }));
  };

  const handleSavePermissions = async () => {
    try {
      if (!selectedRole) return;
      
      setLoading(true);
      await roleService.updateRolePermissions(selectedRole.key || selectedRole.id, editedPermissions);
      
      // Update local state
      setRoles(prev => prev.map(role => 
        role.id === selectedRole.id 
          ? { ...role, permissions: editedPermissions } 
          : role
      ));
      
      toast.success(`Permissions updated for ${selectedRole.name} role`);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error('Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadChanges = async () => {
    try {
      await loadRolesAndUsers();
      toast.success('Roles and users refreshed successfully');
    } catch (error) {
      console.error('Error refreshing roles and users:', error);
      toast.error('Failed to refresh roles and users');
    }
  };

  const handleUserRoleChange = (userId, newRole) => {
    setEditedUserRoles(prev => ({
      ...prev,
      [userId]: newRole
    }));
  };

  const handleSaveUserRole = async (userId) => {
    try {
      if (!editedUserRoles[userId]) {
        toast.error('Please select a role');
        return;
      }

      setLoading(true);
      await userService.updateUserRole(userId, editedUserRoles[userId]);
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, role: editedUserRoles[userId] } 
          : user
      ));
      
      // Clear the edited role for this user
      setEditedUserRoles(prev => {
        const newState = { ...prev };
        delete newState[userId];
        return newState;
      });
      
      toast.success('User role updated successfully');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = roles.filter(role => 
    role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPermissionCount = (role) => {
    return Object.entries(role.permissions || {}).filter(([_, value]) => value === true).length;
  };

  // Check if all page permissions are enabled
  const isPageFullyEnabled = (role, pagePermissions) => {
    if (!role.permissions) return false;
    return pagePermissions.every(permission => role.permissions[permission.id] === true);
  };

  // Check if some but not all page permissions are enabled
  const isPagePartiallyEnabled = (role, pagePermissions) => {
    if (!role.permissions) return false;
    const enabledCount = pagePermissions.filter(permission => role.permissions[permission.id] === true).length;
    return enabledCount > 0 && enabledCount < pagePermissions.length;
  };

  // Toggle all permissions for a page
  const togglePagePermissions = (pagePermissions, value) => {
    const updates = {};
    pagePermissions.forEach(permission => {
      updates[permission.id] = value;
    });
    setEditedPermissions(prev => ({
      ...prev,
      ...updates
    }));
  };

  if (loading && roles.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-b-2 rounded-full animate-spin" style={{ borderColor: BRAND_COLORS.primary }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold" style={{ color: NEUTRAL_COLORS.black }}>
            <Shield className="w-6 h-6" style={{ color: BRAND_COLORS.primary }} />
            Roles & Permissions
          </h1>
          <p className="mt-1" style={{ color: SYSTEM_COLORS.textMuted }}>
            Manage access control by role for all system features
          </p>
        </div>
        <button
          onClick={handleUploadChanges}
          disabled={loading}
          className="flex items-center justify-center w-full gap-2 px-4 py-2 text-white rounded-lg sm:w-auto disabled:opacity-50"
          style={{ backgroundColor: BRAND_COLORS.primary }}
        >
          Refresh Data
        </button>
      </div>

      {/* Tabs */}
      <div className="overflow-x-auto border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        <nav className="flex -mb-px space-x-6 min-w-max sm:space-x-8">
          <button
            onClick={() => setActiveTab('roles')}
            className="px-1 py-4 text-sm font-medium border-b-2 whitespace-nowrap"
            style={{
              borderColor: activeTab === 'roles' ? BRAND_COLORS.primary : 'transparent',
              color: activeTab === 'roles' ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted
            }}
          >
            Role Management
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className="px-1 py-4 text-sm font-medium border-b-2 whitespace-nowrap"
            style={{
              borderColor: activeTab === 'users' ? BRAND_COLORS.primary : 'transparent',
              color: activeTab === 'users' ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted
            }}
          >
            User Management
          </button>
        </nav>
      </div>

      {/* Role Management Tab */}
      {activeTab === 'roles' && (
        <>
          <div className="p-4 mb-6 border-l-4" style={{ borderColor: BRAND_COLORS.accent, backgroundColor: SYSTEM_COLORS.accentTint }}>
            <div className="flex items-start">
              <Info className="h-5 w-5 mt-0.5 mr-3" style={{ color: BRAND_COLORS.accent }} />
              <div>
                <h3 className="font-medium" style={{ color: BRAND_COLORS.accent }}>Role-Based Permissions</h3>
                <p className="mt-1 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                  Changing role permissions will affect all users with that role. Changes take effect immediately.
                </p>
              </div>
            </div>
          </div>

          <div className="relative mb-6">
            <Search className="absolute transform -translate-y-1/2 left-3 top-1/2" size={20} style={{ color: SYSTEM_COLORS.textSoft }} />
            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full py-2 pl-10 pr-4 border rounded-lg focus:outline-none focus:ring-2"
              style={{ borderColor: SYSTEM_COLORS.border, color: NEUTRAL_COLORS.black }}
            />
          </div>

          <div className="space-y-4 lg:hidden">
            {filteredRoles.map(role => (
              <div key={role.id} className="p-4 border rounded-lg shadow-sm" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.primaryTint }}>
                      <span className="text-sm font-medium" style={{ color: BRAND_COLORS.primary }}>
                        {role.name?.charAt(0).toUpperCase() || 'R'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{role.name}</p>
                      {role.isSystem && <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>System Role</p>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleEditRolePermissions(role)}
                    className="text-sm font-medium"
                    style={{ color: BRAND_COLORS.primary }}
                  >
                    Edit
                  </button>
                </div>
                <p className="mt-3 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>{role.description}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold leading-5 rounded-full" style={{ color: BRAND_COLORS.primary, backgroundColor: SYSTEM_COLORS.primaryTint }}>
                    {getPermissionCount(role)} enabled
                  </span>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold leading-5 rounded-full" style={{ color: SYSTEM_COLORS.success, backgroundColor: SYSTEM_COLORS.successTint }}>
                    {getUsersForRole(role).length} users
                  </span>
                </div>
              </div>
            ))}

            {filteredRoles.length === 0 && (
              <div className="px-6 py-10 text-center border rounded-lg" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
                <AlertCircle className="w-10 h-10 mx-auto mb-3" style={{ color: SYSTEM_COLORS.textSoft }} />
                <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>No roles found</p>
              </div>
            )}
          </div>

          <div className="hidden overflow-hidden rounded-lg shadow lg:block" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, boxShadow: SYSTEM_COLORS.shadowSoft }}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                  <tr>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Role
                    </th>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Permissions
                    </th>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Users
                    </th>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRoles.map(role => (
                    <tr key={role.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.primaryTint }}>
                            <span className="text-sm font-medium" style={{ color: BRAND_COLORS.primary }}>
                              {role.name?.charAt(0).toUpperCase() || 'R'}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{role.name}</div>
                            {role.isSystem && (
                              <span className="text-xs text-gray-500">System Role</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{role.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold leading-5 rounded-full" style={{ color: BRAND_COLORS.primary, backgroundColor: SYSTEM_COLORS.primaryTint }}>
                          {getPermissionCount(role)} enabled
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold leading-5 rounded-full" style={{ color: SYSTEM_COLORS.success, backgroundColor: SYSTEM_COLORS.successTint }}>
                          {getUsersForRole(role).length} users
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                        <button
                          onClick={() => handleEditRolePermissions(role)}
                          style={{ color: BRAND_COLORS.primary }}
                        >
                          Edit Permissions
                        </button>
                      </td>
                    </tr>
                  ))}

                  {filteredRoles.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <AlertCircle className="w-12 h-12 mb-4 text-gray-400" />
                          <p className="text-lg text-gray-500">No roles found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="grid gap-4 lg:hidden">
            {users.map(user => (
              <div key={user.id} className="p-4 border rounded-lg shadow-sm" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10">
                    {user.photo_url ? (
                      <img className="w-10 h-10 rounded-full" src={user.photo_url} alt="" />
                    ) : (
                      <div className="flex items-center justify-center w-10 h-10 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                        <span className="text-sm font-medium" style={{ color: SYSTEM_COLORS.textMuted }}>
                          {user.name?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold break-words" style={{ color: NEUTRAL_COLORS.black }}>{user.name || 'Unnamed User'}</p>
                    <p className="mt-1 text-sm break-all" style={{ color: SYSTEM_COLORS.textMuted }}>{user.email}</p>
                    <span className={`mt-2 inline-flex px-2 py-1 text-xs leading-5 font-semibold rounded-full ${
                      user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {user.status || 'unknown'}
                    </span>
                  </div>
                </div>
                <div className="grid gap-3 mt-4">
                  <select
                    value={editedUserRoles[user.id] || user.role || ''}
                    onChange={(e) => handleUserRoleChange(user.id, e.target.value)}
                    className="block w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2"
                    style={{ borderColor: SYSTEM_COLORS.border }}
                  >
                    <option value="">Select Role</option>
                    {roles.map(role => (
                      <option key={role.id} value={role.key}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleSaveUserRole(user.id)}
                    className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium border rounded-md"
                    style={{ color: BRAND_COLORS.primary, borderColor: SYSTEM_COLORS.borderOnBrand, backgroundColor: SYSTEM_COLORS.primaryTint }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ))}

            {users.length === 0 && (
              <div className="px-6 py-10 text-center bg-white border border-gray-200 rounded-lg">
                <AlertCircle className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                <p className="text-sm text-gray-600">No users found</p>
              </div>
            )}
          </div>

          <div className="hidden overflow-hidden rounded-lg shadow lg:block" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, boxShadow: SYSTEM_COLORS.shadowSoft }}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
              <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                <tr>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Current Role
                  </th>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10">
                          {user.photo_url ? (
                            <img className="w-10 h-10 rounded-full" src={user.photo_url} alt="" />
                          ) : (
                            <div className="flex items-center justify-center w-10 h-10 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                              <span className="text-sm font-medium" style={{ color: SYSTEM_COLORS.textMuted }}>
                                {user.name?.charAt(0).toUpperCase() || 'U'}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name || 'Unnamed User'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.status || 'unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={editedUserRoles[user.id] || user.role || ''}
                        onChange={(e) => handleUserRoleChange(user.id, e.target.value)}
                        className="block w-full py-2 pl-3 pr-10 mt-1 text-base border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        <option value="">Select Role</option>
                        {roles.map(role => (
                          <option key={role.id} value={role.key}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                      <button
                        onClick={() => handleSaveUserRole(user.id)}
                        style={{ color: BRAND_COLORS.primary }}
                      >
                        Save Changes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      )}

      {/* Permissions Edit Modal */}
      {isModalOpen && selectedRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}>
          <div className="rounded-lg w-full max-w-4xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, boxShadow: SYSTEM_COLORS.shadowStrong }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="flex items-center gap-2 pr-3 text-lg font-semibold sm:text-xl">
                <Shield className="w-5 h-5" style={{ color: BRAND_COLORS.primary }} />
                Edit Permissions for {selectedRole.name} Role
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-gray-600">{selectedRole.description}</p>
              <p className="mt-1 text-sm text-gray-500">
                {getUsersForRole(selectedRole).length} users have this role
              </p>
            </div>

            {selectedRole.isSystem && selectedRole.key === 'admin' ? (
              <div className="p-4 mb-6 text-center rounded-lg" style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                <Lock className="w-8 h-8 mx-auto mb-2" style={{ color: SYSTEM_COLORS.textSoft }} />
                <p style={{ color: SYSTEM_COLORS.textMuted }}>
                  Admin role automatically has all system permissions.
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-6">
                  {permissionsByPage.map(page => {
                    const isFullyEnabled = isPageFullyEnabled(selectedRole, page.permissions);
                    const isPartiallyEnabled = isPagePartiallyEnabled(selectedRole, page.permissions);
                    
                    return (
                      <div key={page.page} className="p-4 rounded-lg" style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium" style={{ color: NEUTRAL_COLORS.black }}>{page.page}</h3>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => togglePagePermissions(page.permissions, true)}
                              className="text-xs"
                              style={{ color: BRAND_COLORS.primary }}
                            >
                              Enable All
                            </button>
                            <button
                              type="button"
                              onClick={() => togglePagePermissions(page.permissions, false)}
                              className="text-xs text-gray-600 hover:text-gray-800"
                            >
                              Disable All
                            </button>
                            <div className="flex items-center justify-center w-6 h-6 rounded-full">
                              {isFullyEnabled ? (
                                <Check className="w-5 h-5 text-green-600" />
                              ) : isPartiallyEnabled ? (
                                <div className="w-3 h-3 bg-yellow-500 rounded-sm"></div>
                              ) : (
                                <X className="w-5 h-5 text-red-600" />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          {page.permissions.map(permission => (
                            <div key={permission.id} className="flex items-start">
                              <div className="flex items-center h-5">
                                <input
                                  id={permission.id}
                                  type="checkbox"
                                  checked={editedPermissions[permission.id] === true}
                                  onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                                  className="w-4 h-4 border-gray-300 rounded"
                                  style={{ accentColor: BRAND_COLORS.primary }}
                                />
                              </div>
                              <div className="ml-3 text-sm">
                                <label htmlFor={permission.id} className="font-medium text-gray-700">
                                  {permission.label}
                                </label>
                                <p className="mt-1 text-gray-500">
                                  {permission.description}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-col-reverse gap-3 pt-4 mt-6 border-t sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="w-full px-4 py-2 text-gray-700 border border-gray-300 rounded-lg sm:w-auto hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSavePermissions}
                    className="flex items-center justify-center w-full gap-2 px-4 py-2 text-white rounded-lg sm:w-auto"
                    style={{ backgroundColor: BRAND_COLORS.primary }}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (
                      <>
                        <Save size={16} />
                        Save Permissions
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionsControl;
